import { Children, isValidElement, useEffect, useMemo, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, BookOpen, FlaskConical, LoaderCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Link, useNavigate, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { useCapabilities } from "../context/CapabilitiesContext";
import { usePortal } from "../context/PortalContext";
import { api } from "../lib/api";

type TocItem = { id: string; label: string; level: "part" | "module" };

function headingId(label: string): string {
  const moduleMatch = label.match(/^模块\s+([1-9])：/);
  if (moduleMatch) return `module-${moduleMatch[1]}`;
  if (label.includes("第一篇")) return "panorama";
  if (label.includes("第二篇")) return "modules";
  if (label.includes("第三篇")) return "learning-path";
  return label.replace(/[\s：、“”]/g, "-").replace(/[^\w\u4e00-\u9fff-]/g, "").toLowerCase();
}

function nodeText(children: ReactNode): string {
  return Children.toArray(children).map((child) => typeof child === "string" ? child : "").join("");
}

function MermaidFlow({ source }: { source: string }) {
  const labels = Array.from(source.matchAll(/\["([^"]+)"\]/g), (match) => match[1]);
  const uniqueLabels = labels.filter((label, index) => labels.indexOf(label) === index);
  if (!uniqueLabels.length) return <pre><code>{source}</code></pre>;
  return (
    <div className="textbook-flow" role="img" aria-label={uniqueLabels.join("，然后 ")}>
      {uniqueLabels.map((label, index) => (
        <div className="textbook-flow-step" key={label}>
          <span>{String(index + 1).padStart(2, "0")}</span>
          <strong>{label}</strong>
        </div>
      ))}
    </div>
  );
}

function MarkdownPre({ children }: { children?: ReactNode }) {
  const child = Children.only(children);
  if (isValidElement<{ className?: string; children?: ReactNode }>(child) && child.props.className === "language-mermaid") {
    return <MermaidFlow source={nodeText(child.props.children)} />;
  }
  return <pre>{children}</pre>;
}

function buildToc(markdown: string): TocItem[] {
  return markdown.split("\n").flatMap<TocItem>((line) => {
    const part = line.match(/^# (第一篇|第三篇)：(.+)$/);
    if (part) {
      const label = `${part[1]}：${part[2]}`;
      return [{ id: headingId(label), label, level: "part" as const }];
    }
    const module = line.match(/^## (模块 [1-9]：.+)$/);
    if (module) return [{ id: headingId(module[1]), label: module[1], level: "module" as const }];
    const section = line.match(/^## ([一二三四五六七八九十百]+、.+)$/);
    if (section) return [{ id: headingId(section[1]), label: section[1], level: "module" as const }];
    return [];
  });
}

function isTextbookKind(kind: string): boolean {
  return kind === "textbook" || kind === "module-textbook";
}

function scrollToHeading(id: string) {
  document.getElementById(id)?.scrollIntoView();
}

export function TextbookPage() {
  const { token = "" } = useParams();
  const { track } = usePortal();
  const capabilities = useCapabilities();
  const navigate = useNavigate();
  const artifact = track?.published_artifacts.find((item) => item.token === token && isTextbookKind(item.kind));
  const isModule = artifact?.kind === "module-textbook";
  const moduleNumber = artifact?.module_number;
  const firstModule = track?.published_artifacts
    .filter((item) => item.kind === "module-textbook")
    .sort((left, right) => (left.module_number ?? 999) - (right.module_number ?? 999))[0];
  const [markdown, setMarkdown] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let active = true;
    setMarkdown("");
    setError(null);
    if (!artifact?.url) {
      setError("教材不存在或尚未发布");
      return () => { active = false; };
    }
    fetch(artifact.url)
      .then((response) => {
        if (!response.ok) throw new Error(`教材读取失败（${response.status}）`);
        return response.text();
      })
      .then((text) => { if (active) setMarkdown(text); })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : String(reason)); });
    return () => { active = false; };
  }, [artifact?.token, artifact?.url]);

  const toc = useMemo(() => buildToc(markdown), [markdown]);

  async function startPractice() {
    if (!track || !artifact?.practice_deck_id) return;
    setStarting(true);
    setError(null);
    try {
      const session = await api.startSession(track.slug, { deck: artifact.practice_deck_id });
      navigate(`/learn/session/${session.id}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
      setStarting(false);
    }
  }

  if (!track || !markdown) {
    return <div className="loading-state"><LoaderCircle className={error ? "" : "spin"} size={20} />{error || "正在读取教材"}</div>;
  }

  return (
    <div className="textbook-shell">
      <header className="textbook-masthead">
        <Link to="/resources" className="textbook-back"><ArrowLeft size={16} />返回教材资料</Link>
        <div className="textbook-kicker"><BookOpen size={17} />{isModule ? `模块教材 · ${String(moduleNumber ?? "").padStart(2, "0")}` : "核心教材 · V0.1 教材先行版"}</div>
        <h1>{isModule ? artifact.masthead_title || artifact.label : "先建立全景，再进入模块"}</h1>
        <p>{isModule
          ? artifact.masthead_description || "先理解本模块的知识和案例，再用选择题检查判断。"
          : "从产品为何存在开始，沿九个模块走到发布与持续改进。阅读只代表接触知识，不代表能力已经通过。"}</p>
      </header>
      <div className="textbook-grid">
        <aside className="textbook-outline" aria-label="教材目录">
          <strong>{isModule ? "本章学习地图" : "全景学习地图"}</strong>
          {toc.map((item) => <button
            className={item.level === "part" ? "is-part" : ""}
            key={item.id}
            type="button"
            onClick={() => scrollToHeading(item.id)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                scrollToHeading(item.id);
              }
            }}
          >{item.label}</button>)}
        </aside>
        <article className="textbook-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => <h1 id={headingId(nodeText(children))}>{children}</h1>,
              h2: ({ children }) => <h2 id={headingId(nodeText(children))}>{children}</h2>,
              h3: ({ children }) => <h3 id={headingId(nodeText(children))}>{children}</h3>,
              pre: MarkdownPre,
            }}
          >{markdown}</ReactMarkdown>
          <div className="textbook-next">
            {isModule ? <>
              <span>下一步 · 只做选择题与情境判断</span>
              <h2>确认你是否理解了本模块的关键判断</h2>
              <p>这组练习只用于指出理解偏差，不产生正式掌握记录。</p>
              <button className="command-button" type="button" disabled={!capabilities.practice.writable || starting || !artifact.practice_deck_id} onClick={() => void startPractice()}>
                <FlaskConical size={17} />{!capabilities.practice.writable ? "当前模式不可练习" : starting ? "正在开始" : `开始模块 ${moduleNumber ?? ""} 选择题`}
              </button>
            </> : firstModule ? <>
              <span>下一步 · 模块 01</span>
              <h2>进入第一本详细教材</h2>
              <p>先理解现实中的产品问题如何形成，再进入价值判断、方案定义和项目训练。</p>
              <Link className="command-button" to={`/resources/textbook/${firstModule.token}`}>阅读模块 1<ArrowRight size={17} /></Link>
            </> : null}
            {error && <p className="form-error" role="alert">{error}</p>}
          </div>
        </article>
      </div>
    </div>
  );
}
